import React from 'react';
import {useQueries, useQuery} from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';

import {getLocation} from 'common/util/url';
import {MetricResults, useExperimentsStore} from 'apps/experiments/src/store';
import {exposuresQueryKey, statsigQueryKey} from 'apps/experiments/src/react-query';
import {queryModule} from 'common/report/report-manager/query';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {
  ExperimentMetricResults,
  FlattenedExperimentMetricResults,
  StatsigSeriesResults,
} from 'apps/experiments/src/types';
import {DEFAULT_CONTROL_KEY} from 'apps/experiments/src/components/utils';
import {
  createExposuresBookmarkParams,
  createStatsigBookmarkParams,
  getLatestSeriesValues,
  InsightsBookmarkParamsWithMetricId,
  transformStatsigResults,
} from 'apps/experiments/src/components/query-utils';

import {useTestingModelModifiers} from './testing-model-modifiers';
import {useExperimentSettings} from './experiment-settings';
import {useExperimentGlobalFilters} from './global-filters';
import {useStagedExperiment} from './staged-experiment';

class ExperimentMetricError extends Error {
  public readonly metricId: string = ``;

  constructor(message: string, metricId: string, options?: ErrorOptions) {
    super(message, options);
    this.metricId = metricId;
    this.name = `ExperimentMetricError`;
  }

  static fromError(error: Error, metricId?: string): ExperimentMetricError {
    if (error instanceof ExperimentMetricError) {
      return error;
    }
    return new ExperimentMetricError(error.message, metricId ?? ``, {cause: error});
  }
}

type StoreMetricResultsParams = {
  projectId: number;
  workspaceId: number;
  experimentId: string;
};
export const storeMetricResults = (params: StoreMetricResultsParams) => {
  const {stagedExperiment: experiment} = useStagedExperiment();
  const _metricResults = useExperimentsStore((state) => state.metricResults);
  const setMetricResults = useExperimentsStore((state) => state.setMetricResults);
  const _isLoadingMetricResults = useExperimentsStore((state) => state.isLoadingMetricResults);
  const setIsLoadingMetricResults = useExperimentsStore((state) => state.setIsLoadingMetricResults);
  const _exposuresBookmarkParams = useExperimentsStore((state) => state.exposuresBookmarkParams);
  const setExposuresBookmarkParams = useExperimentsStore((state) => state.setExposuresBookmarkParams);
  const _exposuresQueryResults = useExperimentsStore((state) => state.exposuresQueryResults);
  const setExposuresQueryResults = useExperimentsStore((state) => state.setExposuresQueryResults);
  const currentFeatureFlag = useExperimentsStore((state) => state.currentFeatureFlag);
  const {additionalFilters} = useExperimentGlobalFilters();
  const {projectId, workspaceId, experimentId} = params;
  const {pathname, search, hash} = getLocation();
  const {noPeeking} = useTestingModelModifiers();
  const experimentSettings = useExperimentSettings();

  const controlKey = experiment?.settings.controlKey ?? DEFAULT_CONTROL_KEY;

  const exposuresBookmarkParams = React.useMemo(() => {
    if (!experiment) {
      return undefined;
    }
    return createExposuresBookmarkParams({
      experiment,
      featureFlag: currentFeatureFlag,
      experimentEventName: experimentSettings.eventName,
      experimentNameProperty: experimentSettings.nameProperty?.value,
    });
  }, [experiment, currentFeatureFlag, experimentSettings]);

  const {data: exposuresQueryResults = null, isSuccess: exposuresSuccess} = useQuery({
    queryKey: exposuresQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      experimentId: experimentId!,
      bookmarkParams: exposuresBookmarkParams,
    }),
    enabled: !!exposuresBookmarkParams,
    queryFn: () => {
      const queryParams: Obj = {
        project_id: String(projectId),
        ...(workspaceId ? {workspace_id: workspaceId} : {}),
      };
      return queryModule
        .executeReportQuery({
          endpoint: BookmarkType.Insights,
          body: {
            bookmark: exposuresBookmarkParams,
            ...queryParams,
            use_query_cache: true,
            use_query_sampling: false,
            report_query_origin: BookmarkType.Insights,
            tracking_props: {},
          },
          throwOnInvalidStatus: true,
          location: {
            pathname,
            search,
            hash,
            key: ``,
            state: ``,
          },
        })
        .then((response) => {
          return response.json();
        });
    },
  });

  const statsigBookmarkParams = React.useMemo((): InsightsBookmarkParamsWithMetricId[] => {
    const exposures = getLatestSeriesValues(exposuresQueryResults);
    if (!Object.values(exposures).length) {
      return [];
    }
    return (
      experiment?.metrics
        .map((metric) =>
          createStatsigBookmarkParams({
            experiment,
            metric,
            controlKey,
            exposures,
            featureFlag: currentFeatureFlag,
            additionalFilters,
          }),
        )
        .filter(
          (v: InsightsBookmarkParamsWithMetricId | undefined): v is InsightsBookmarkParamsWithMetricId =>
            v !== undefined,
        ) ?? []
    );
  }, [experiment, exposuresQueryResults, controlKey, currentFeatureFlag, additionalFilters]);

  const statsigQueryResults = useQueries({
    queries: statsigBookmarkParams.map((bookmarkParams) => {
      const {metricId, ...cleanBookmarkParams} = bookmarkParams;
      return {
        queryKey: statsigQueryKey({
          projectId: projectId!,
          workspaceId: workspaceId!,
          experimentId: experimentId!,
          bookmarkParams,
        }),
        enabled: exposuresSuccess && !!bookmarkParams,
        queryFn: async () => {
          const queryParams: Obj = {
            project_id: String(projectId),
            ...(workspaceId ? {workspace_id: workspaceId} : {}),
          };
          try {
            const response = await queryModule.executeReportQuery({
              endpoint: BookmarkType.Insights,
              queryParams,
              body: {
                bookmark: cleanBookmarkParams,
                ...queryParams,
                use_query_cache: true,
                use_query_sampling: false,
                report_query_origin: BookmarkType.Insights,
                tracking_props: {},
              },
              throwOnInvalidStatus: true,
              location: {
                pathname,
                search,
                hash,
                key: ``,
                state: ``,
              },
            });
            return {
              ...(await response.json()),
              metricId,
            };
          } catch (error) {
            // We end up using the error message to determine what type of error tooltip
            // to show.  If it's an unknown error message (like one that even comes from
            // attempting to parse the error response json) we don't care because we have
            // a fallback error message and we are tracking all error messages in MixPanel
            const resp = await error.response.json();
            throw new ExperimentMetricError(resp?.error, metricId);
          }
        },
      };
    }),
  });

  const isLoadingMetricResults = statsigQueryResults.some((query) => query.isPending);
  if (isLoadingMetricResults !== _isLoadingMetricResults) {
    setIsLoadingMetricResults(isLoadingMetricResults);
  }

  const nonTreatmentKeys = new Set([`undefined`, controlKey, RESULTS_OVERALL_SUM_KEY]);

  const metricResults: MetricResults = React.useMemo(() => {
    return statsigQueryResults.reduce((metricsMap, query, index) => {
      if (query.isSuccess) {
        const metricId = query.data.metricId;
        const results = Object.values((query.data.series ?? {}) as StatsigSeriesResults)[0];
        if (!results) {
          return metricsMap;
        }
        metricsMap[metricId] = Object.keys(results).reduce(
          (variantsMap, key) => {
            if (nonTreatmentKeys.has(key) || !statsigBookmarkParams[index]) {
              return variantsMap;
            }
            const statsigResults = transformStatsigResults({
              results,
              controlKey,
              treatmentKey: key,
              hideSignificance: noPeeking,
            });
            if (!statsigResults) {
              return variantsMap;
            }
            variantsMap[key] = statsigResults;
            return variantsMap;
          },
          {} as Record<string, ExperimentMetricResults>,
        );
      } else if (query.isError) {
        const error = ExperimentMetricError.fromError(query.failureReason ?? query.error);
        metricsMap[error.metricId] = error;
      }
      return metricsMap;
    }, {} as MetricResults);
  }, [statsigQueryResults, experiment?.metrics, controlKey, noPeeking]);

  if (!isEqual(exposuresBookmarkParams, _exposuresBookmarkParams)) {
    setExposuresBookmarkParams(exposuresBookmarkParams);
  }
  if (!isEqual(exposuresQueryResults, _exposuresQueryResults)) {
    setExposuresQueryResults(exposuresQueryResults);
  }
  if (!isEqual(metricResults, _metricResults)) {
    setMetricResults(metricResults);
  }
};

export const useFlattenedMetricResults = () => {
  const metricResults = useExperimentsStore((state) => state.metricResults);
  const flattenedMetrics: FlattenedExperimentMetricResults[] = [];
  Object.entries(metricResults).forEach(([metricId, results]) => {
    Object.entries(results).forEach(([variant, result]) => {
      if (result.notSupported) {
        return;
      }
      flattenedMetrics.push({
        ...result,
        metricId,
        variant,
      });
    });
  });
  return flattenedMetrics;
};

export const useExposuresSeriesName = () => {
  const exposuresQueryResults = useExperimentsStore((state) => state.exposuresQueryResults);
  return Object.keys(exposuresQueryResults?.series ?? {})[0];
};

export const useVariants = () => {
  const exposuresQueryResults = useExperimentsStore((state) => state.exposuresQueryResults);
  const {stagedExperiment: experiment} = useStagedExperiment();

  const controlKey = experiment?.settings.controlKey ?? DEFAULT_CONTROL_KEY;

  const nonTreatmentKeys = React.useMemo(
    () => new Set([`undefined`, controlKey, RESULTS_OVERALL_SUM_KEY]),
    [controlKey],
  );

  return React.useMemo(() => {
    if (!exposuresQueryResults) {
      return new Set<string>();
    }
    const allSeriesKeys = Object.keys(getLatestSeriesValues(exposuresQueryResults));
    return new Set(
      Array.from(new Set<string>(allSeriesKeys).difference(nonTreatmentKeys)).sort((a, b) => {
        return a.localeCompare(b);
      }),
    );
  }, [exposuresQueryResults, nonTreatmentKeys]);
};
